import { useContext, useEffect, useState } from 'react';
import { toCurrency } from '@app/utilities';
import { AccountContext } from '../AccountContext';
import { Segment } from 'semantic-ui-react';
import { useGetAccountEffectiveRates, useGetAccruedInterestForBillingPeriod, useGetLedger } from '@app/sdk';
import { CopyToClipboardIcon } from '../../../shared/CopyToClipboardIcon';

const SubledgerBalances = () => {
  const { accountId } = useContext(AccountContext);
  const [ledger, setLedger] = useState<Ledger>({ ledgerTotal: 0 } as any);
  const [subledgerEffectiveRates, setSubledgerEffectiveRates] = useState(undefined as any);
  const [accruedInterest, setAccruedInterest] = useState({} as any);
  const { data: accountEffectiveRates } = useGetAccountEffectiveRates({ accountId });

  const { data: ledgerResponse } = useGetLedger({ accountId });
  const { data: accruedInterestResponse } = useGetAccruedInterestForBillingPeriod({ accountId });

  useEffect(() => {
    if (accountEffectiveRates) {
      const mappedRates = accountEffectiveRates.subledgerRates.reduce((obj, sl) => {
        return {
          ...obj,
          [sl.subledgerId]: sl.rate.derivedEffectiveRate.air,
        };
      }, []);
      setSubledgerEffectiveRates(mappedRates);
    } else {
      setSubledgerEffectiveRates(undefined);
    }
  }, [accountEffectiveRates]);

  useEffect(() => {
    if (ledgerResponse) {
      const ledgerTotal = ledgerResponse.subledgers.reduce((a, b) => a + b.total.value, 0);
      setLedger({ ledgerTotal, ...ledgerResponse });
    }
  }, [ledgerResponse]);

  useEffect(() => {
    if (!accruedInterestResponse) {
      setAccruedInterest({});
      return;
    }

    const accruedInterestBySubLedger = {};

    accruedInterestResponse?.dailyAccruals?.forEach((dailyAccrual) => {
      dailyAccrual.subledgers.forEach((subledger) => {
        let newInterest: number;

        if (accruedInterestBySubLedger[subledger.subledgerId] === undefined) {
          newInterest = 0;
        } else {
          newInterest = accruedInterestBySubLedger[subledger.subledgerId];
        }

        newInterest += subledger.dailyInterest.total;
        accruedInterestBySubLedger[subledger.subledgerId] = newInterest;
      });
    });

    setAccruedInterest(accruedInterestBySubLedger);
  }, [accruedInterestResponse]);

  return (
    <Segment>
      <h2 className="ui header">
        Subledger Balances
        {ledger && <div className="sub header">Total {toCurrency(ledger.ledgerTotal)}</div>}
      </h2>
      {ledger && (
        <>
          <table className="ui compact table">
            <thead>
              <tr>
                <th>Subledger</th>
                <th>Description</th>
                <th>Statemented</th>
                <th>Non-Statemented</th>
                <th>Accrued Interest</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {ledger.subledgers?.map((s) => (
                <tr key={s.subledgerId}>
                  <td>
                    {s.subledgerType ? s.subledgerType : s.spendType} {subledgerEffectiveRates && subledgerEffectiveRates[s.subledgerId]}
                    <div className="ui grey sub header">{s.subledgerId}<CopyToClipboardIcon value={s.subledgerId} /></div>
                  </td>
                  <td>
                    {s.subledgerDescription}
                  </td>
                  <td>
                    <div className="ui horizontal list">
                      <div className="ui label">
                        Principal
                        <div className="detail">{toCurrency(s.statemented.Principal.value)}</div>
                      </div>
                      <div className="ui label">
                        Interest
                        <div className="detail">{toCurrency(s.statemented.Interest.value)}</div>
                      </div>
                      <div className="ui label">
                        Fee
                        <div className="detail">{toCurrency(s.statemented.Fee?.value)}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="ui horizontal list">
                      <div className="ui label">
                        Principal
                        <div className="detail">{toCurrency(s.nonStatemented.Principal.value)}</div>
                      </div>
                      <div className="ui label">
                        Interest
                        <div className="detail">{toCurrency(s.nonStatemented.Interest.value)}</div>
                      </div>
                      <div className="ui label">
                        Fee
                        <div className="detail">{toCurrency(s.nonStatemented.Fee?.value)}</div>
                      </div>
                    </div>
                  </td>
                  <td>{toCurrency(accruedInterest[s.subledgerId])}</td>
                  <td>{toCurrency(s.total.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Segment>
  );
};

export default SubledgerBalances;
